<script setup lang="ts">
// oxlint-disable import/no-unassigned-import
import {Swiper, SwiperSlide} from 'swiper/vue'
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import {Pagination, Navigation} from 'swiper/modules'
import Next from '~/assets/icons/next.svg?component'
import Prev from '~/assets/icons/prev.svg?component'

import type {WorkshopCarouselCard} from '~/types/workshop'

const props = defineProps<{
  items: WorkshopCarouselCard[] | undefined
}>()
</script>

<template>
  <div class="carousel-wrapper max-w-[280px]! mx-auto sm:max-w-full">
    <div class="relative top-96">
      <button class="swiper-button-prev" aria-label="Previous slide"><Prev /></button>
      <button class="swiper-button-next" aria-label="Next slide"><Next /></button>
    </div>
    <swiper
      ref="swiperRef"
      :loop="false"
      :grabCursor="true"
      :centeredSlides="false"
      :modules="[Pagination, Navigation]"
      :navigation="{
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next',
      }"
      :spaceBetween="16"
      :slidesPerView="6"
      :breakpoints="{
        320: {slidesPerView: 1.2, spaceBetween: 12, slidesOffsetBefore: 0},
        480: {slidesPerView: 1.5, spaceBetween: 16, slidesOffsetBefore: 0},
        768: {slidesPerView: 2.5, spaceBetween: 16, slidesOffsetBefore: 0},
        1024: {slidesPerView: 3, spaceBetween: 20, slidesOffsetBefore: 0},
        1280: {slidesPerView: 4, spaceBetween: 20, slidesOffsetBefore: 190},
      }"
    >
      <swiper-slide
        style="height: 300px !important; width: 280px !important"
        v-for="(item, i) in props.items"
        :key="item.title! + i"
        class="card flex flex-col justify-start rounded-lg border border-green-200 bg-green-50 p-4"
      >
        <div class="icon-container mb-4 flex items-center">
          <Icon class="h-10 w-10 rounded-lg bg-main-600 p-2 text-white" :name="item.icon || ''" />
        </div>
        <div class="flex flex-1 flex-col justify-center">
          <Heading class="mb-2 text-left" as="h4" size="small">{{ item.title }}</Heading>
          <Paragraph size="normal" class="text-left text-gray-600">{{ item.description }}</Paragraph>
        </div>
      </swiper-slide>
    </swiper>
  </div>
</template>

<style lang="postcss">
.carousel-wrapper {
  @apply max-w-full;
}

.swiper {
  @apply mr-5 p-2;
  --carousel-item-width: 280px !important;
}

.card {
  @apply flex flex-col justify-between overflow-hidden rounded-md border border-main-200 bg-main-50 p-4;
}

.text-container {
  @apply flex flex-col justify-end;
}

.swiper-slide {
  width: 280px !important;
  height: 300px !important;
}

.swiper-button-prev::after,
.swiper-button-next::after {
  content: none;
}

.swiper-button-prev,
.swiper-button-next {
  @apply flex items-center justify-center rounded-md border bg-gray-100 p-3 shadow-md hover:bg-gray-300 focus:outline-none;
  width: 44px;
  height: 44px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
}

.swiper-button-prev {
  @apply xs:left-[30px] xl:left-[400px];
}

.swiper-button-next {
  @apply xs:left-[100px] xl:left-[470px];
}

.swiper-button-prev svg,
.swiper-button-next svg {
  @apply h-6 w-6 text-gray-600;
}

.swiper-button-prev:hover,
.swiper-button-next:hover {
  @apply bg-gray-300;
}

.swiper-button-prev:focus,
.swiper-button-next:focus {
  @apply outline-none ring-2 ring-main-500 ring-offset-2;
}
</style>
